import { cn } from '../lib/cn.js';
import { THRESHOLD_STROKE, thresholdTone } from '../lib/tones.js';

export interface CoverageRingProps {
  /** Coverage ratio 0..1. `null` renders an empty ring with `--`. */
  value: number | null | undefined;
  /** Accessible label prefix, e.g. `Control coverage`. */
  label?: string;
  className?: string;
}

const R = 18;
const C = 2 * Math.PI * R;

/** 48px ring, 3px stroke, square ends, integer percent without a sign (DESIGN_SYSTEM section 9.2). */
export function CoverageRing({ value, label = 'Coverage', className }: CoverageRingProps) {
  const known = typeof value === 'number' && Number.isFinite(value);
  const v = known ? Math.min(1, Math.max(0, value)) : 0;
  const tone = thresholdTone(v);
  const pct = Math.round(v * 100);
  const text = known ? `${pct}` : '--';
  return (
    <span
      role="img"
      aria-label={known ? `${label} ${pct} percent` : `${label} unknown`}
      className={cn('relative inline-block h-12 w-12 shrink-0', className)}
    >
      <svg viewBox="0 0 48 48" className="block h-full w-full -rotate-90">
        <circle cx={24} cy={24} r={R} fill="none" stroke="var(--hair)" strokeWidth={3} />
        {known ? (
          <circle
            cx={24}
            cy={24}
            r={R}
            fill="none"
            stroke={THRESHOLD_STROKE[tone]}
            strokeWidth={3}
            strokeLinecap="butt"
            strokeDasharray={`${(C * v).toFixed(3)} ${(C * (1 - v)).toFixed(3)}`}
          />
        ) : null}
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-mono text-xs-plus tabular-nums text-ink">{text}</span>
    </span>
  );
}
